<script>
  import gql from 'graphql-tag'

  const MAX_FIELDS = 4
  const TYPES = ['string', 'numerical']

  const TYPE_META = {
    string: { glyph: 'Aa', label: 'Text' },
    numerical: { glyph: '0–9', label: 'Number' }
  }

  export default {
    props: ['gid'],
    apollo: {
      definitions: {
        fetchPolicy: 'no-cache',
        query: gql`query($gid: ID) {
          merchant(gid: $gid) { merchantUser { settings {
            customFieldDefinitions { label type required }
          }}}
        }`,
        variables() { return { gid: this.gid } },
        update: data => data.merchant.merchantUser.settings.customFieldDefinitions || []
      }
    },
    data() {
      return {
        definitions: [],
        form: [],
        editing: false,
        MAX_FIELDS,
        TYPES,
        TYPE_META
      }
    },
    computed: {
      canAddMore() { return this.form.length < MAX_FIELDS },
      slotsLeft() { return MAX_FIELDS - this.form.length }
    },
    methods: {
      openEdit() {
        this.form = this.definitions.map(d => ({
          label: d.label, type: d.type, required: !!d.required
        }))
        this.editing = true
        this.$nextTick(() => M.updateTextFields())
      },
      cancelEdit() {
        this.editing = false
        this.form = []
      },
      addRow() {
        if (!this.canAddMore) return
        this.form.push({ label: '', type: 'string', required: false })
        this.$nextTick(() => M.updateTextFields())
      },
      removeRow(idx) {
        this.form.splice(idx, 1)
      },
      moveUp(idx) {
        if (idx <= 0) return
        const row = this.form.splice(idx, 1)[0]
        this.form.splice(idx - 1, 0, row)
      },
      moveDown(idx) {
        if (idx >= this.form.length - 1) return
        const row = this.form.splice(idx, 1)[0]
        this.form.splice(idx + 1, 0, row)
      },
      typeLabel(type) {
        return (TYPE_META[type] || {}).label || type
      },
      save() {
        this.$apollo.mutate({
          mutation: gql`mutation ($definitions: [CustomFieldDefinitionInput!]!) {
            updateCustomFieldDefinitions(input: { definitions: $definitions }) {
              customFieldDefinitions { label type required }
              errors
            }
          }`,
          variables: {
            definitions: this.form.map(d => ({
              label: d.label, type: d.type, required: !!d.required
            }))
          }
        }).then(result => {
          const payload = result.data.updateCustomFieldDefinitions
          if (payload.errors && payload.errors.length) {
            this.notifyError(payload.errors.join(', '))
            return
          }
          this.definitions = payload.customFieldDefinitions || []
          this.editing = false
          this.notifySuccess('Custom fields updated')
        }, error => {
          this.gqlErrorHandler(error)
        })
      }
    }
  }
</script>

<template>
  <div class="cf-panel" v-if="!$apollo.queries.definitions.loading">
    <div class="cf-head">
      <div class="cf-head-text">
        <div class="cf-title">Custom transaction fields</div>
        <div class="cf-sub">Up to {{ MAX_FIELDS }} merchant-defined fields shown on payment forms.</div>
      </div>
      <a v-if="!editing && definitions.length" class="cf-edit-link pointer" @click="openEdit">
        <i class="far fa-edit"></i> Edit
      </a>
    </div>

    <!-- READ VIEW -->
    <div v-if="!editing">
      <ul v-if="definitions.length" class="cf-list">
        <li v-for="d in definitions" :key="d.label" class="cf-list-row">
          <span class="cf-list-label">{{ d.label }}</span>
          <span class="cf-chip cf-chip-type">
            <span class="cf-glyph">{{ (TYPE_META[d.type] || {}).glyph }}</span>
            {{ typeLabel(d.type) }}
          </span>
          <span v-if="d.required" class="cf-chip cf-chip-required">Required</span>
        </li>
      </ul>
      <div v-else class="cf-empty">
        <p class="cf-empty-text">No custom fields configured yet.</p>
        <button class="btn-small emerald" @click="openEdit">
          <i class="fas fa-plus"></i> Configure fields
        </button>
      </div>
    </div>

    <!-- EDIT VIEW -->
    <div v-else>
      <div v-if="form.length" class="cf-table">
        <div class="cf-thead">
          <span class="cf-col-order"></span>
          <span class="cf-col-label">Label</span>
          <span class="cf-col-type">Type</span>
          <span class="cf-col-required">Required</span>
          <span class="cf-col-actions"></span>
        </div>

        <div v-for="(row, idx) in form" :key="idx" class="cf-row">
          <div class="cf-col-order">
            <button type="button" class="cf-order-btn pointer"
                    :disabled="idx === 0"
                    :title="'Move ' + (row.label || 'field') + ' up'"
                    @click="moveUp(idx)">
              <i class="fas fa-chevron-up"></i>
            </button>
            <button type="button" class="cf-order-btn pointer"
                    :disabled="idx === form.length - 1"
                    :title="'Move ' + (row.label || 'field') + ' down'"
                    @click="moveDown(idx)">
              <i class="fas fa-chevron-down"></i>
            </button>
          </div>

          <div class="cf-col-label">
            <input
              :id="'cf_label_' + idx"
              type="text"
              maxlength="30"
              v-model="row.label"
              placeholder="e.g. PO Number"
              class="cf-text-input">
          </div>

          <div class="cf-col-type">
            <div class="cf-segmented" role="radiogroup" aria-label="Field validation type">
              <button
                v-for="t in TYPES"
                :key="t"
                type="button"
                class="cf-seg"
                :class="{ 'cf-seg-active': row.type === t }"
                role="radio"
                :aria-checked="row.type === t"
                @click="row.type = t">
                <span class="cf-glyph">{{ TYPE_META[t].glyph }}</span>
                <span class="cf-seg-label">{{ TYPE_META[t].label }}</span>
              </button>
            </div>
          </div>

          <div class="cf-col-required">
            <div class="switch cf-switch">
              <label>
                <input type="checkbox" v-model="row.required">
                <span class="lever"></span>
              </label>
            </div>
          </div>

          <div class="cf-col-actions">
            <a class="cf-remove pointer" :title="'Remove ' + (row.label || 'field')" @click="removeRow(idx)">
              <i class="fas fa-times"></i>
            </a>
          </div>
        </div>
      </div>

      <div v-else class="cf-empty-edit grey-text">
        Add your first field below.
      </div>

      <div class="cf-toolbar">
        <button
          class="btn-flat cf-add-btn"
          @click="addRow"
          :disabled="!canAddMore">
          <i class="fas fa-plus"></i> Add field
          <span class="cf-slots" v-if="canAddMore">· {{ slotsLeft }} {{ slotsLeft === 1 ? 'slot' : 'slots' }} left</span>
          <span class="cf-slots" v-else>· max reached</span>
        </button>
      </div>

      <div class="cf-actions">
        <button class="btn-small emerald" @click="save" :disabled="$apollo.loading">
          Save changes
        </button>
        <a class="pointer grey-text cf-cancel" @click="cancelEdit">Cancel</a>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .cf-panel {
    --cf-line: rgba(0, 0, 0, 0.08);
    --cf-muted: #8a8f99;
    --cf-ink: #1e2329;
    --cf-emerald: #00ab8e;
    --cf-emerald-soft: rgba(0, 171, 142, 0.08);
    --cf-surface: #fafbfc;
    text-align: left;
  }
  .cf-panel * { box-sizing: border-box; }

  .cf-head {
    display: flex;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 14px;
    width: 100%;
    text-align: left !important;
  }

  .cf-head-text {
    flex: 1 1 auto;
    min-width: 0;
    text-align: left !important;
  }

  .cf-title {
    margin: 0 0 4px;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--cf-ink);
    text-align: left !important;
    line-height: 1.4;
  }

  .cf-sub {
    margin: 0;
    color: var(--cf-muted);
    font-size: 13px;
    line-height: 1.45;
    max-width: 60ch;
    text-align: left !important;
  }

  .cf-edit-link {
    flex-shrink: 0;
    margin-left: auto;
    font-size: 13px;
    color: var(--cf-muted);
    transition: color 120ms ease;
  }
  .cf-edit-link:hover { color: var(--cf-ink); }

  /* READ VIEW */
  .cf-list {
    list-style: none;
    margin: 0;
    padding: 0;
    border-top: 1px solid var(--cf-line);
  }

  .cf-list-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid var(--cf-line);
  }

  .cf-list-label {
    flex: 1;
    font-weight: 500;
    color: var(--cf-ink);
    font-size: 14px;
  }

  .cf-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    line-height: 1.6;
  }

  .cf-chip-type {
    background: rgba(0, 0, 0, 0.05);
    color: var(--cf-ink);
  }
  .cf-chip-required {
    background: var(--cf-emerald-soft);
    color: var(--cf-emerald);
  }

  .cf-glyph {
    font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 0;
    color: inherit;
    opacity: 0.85;
  }

  .cf-empty {
    border-top: 1px dashed var(--cf-line);
    padding: 28px 0 8px;
    text-align: center;
  }
  .cf-empty-text {
    margin: 0 0 12px;
    color: var(--cf-muted);
    font-size: 13px;
  }

  /* EDIT VIEW */
  .cf-table {
    border-top: 1px solid var(--cf-line);
    margin-bottom: 4px;
  }

  .cf-thead {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) 220px 100px 36px;
    gap: 16px;
    padding: 12px 0 8px;
    color: var(--cf-muted);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    border-bottom: 1px solid var(--cf-line);
  }

  .cf-row {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) 220px 100px 36px;
    gap: 16px;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid var(--cf-line);
  }

  .cf-text-input {
    width: 100%;
    height: 36px;
    margin: 0;
    padding: 0 0 4px;
    font-size: 14px;
    line-height: 1.4;
    color: var(--cf-ink);
    background: transparent;
    border: 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 0;
    box-shadow: none;
    outline: none;
    transition: border-color 120ms ease, box-shadow 120ms ease;
  }
  .cf-text-input::placeholder {
    color: var(--cf-muted);
    opacity: 0.7;
  }
  .cf-text-input:focus,
  .cf-text-input:focus-visible {
    border-bottom: 1px solid var(--cf-emerald);
    box-shadow: 0 1px 0 0 var(--cf-emerald);
    outline: none;
  }

  /* Segmented type control */
  .cf-segmented {
    display: inline-flex;
    border: 1px solid var(--cf-line);
    border-radius: 8px;
    background: var(--cf-surface);
    overflow: hidden;
    height: 36px;
  }

  .cf-seg {
    appearance: none;
    background: transparent;
    border: 0;
    padding: 0 12px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    color: var(--cf-muted);
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0;
    transition: background 120ms ease, color 120ms ease;
    border-right: 1px solid var(--cf-line);
  }
  .cf-seg:last-child { border-right: 0; }
  .cf-seg:hover { color: var(--cf-ink); }

  .cf-seg-active {
    background: #ffffff;
    color: var(--cf-ink);
    box-shadow: inset 0 -2px 0 0 var(--cf-emerald);
  }
  .cf-seg-active .cf-glyph { color: var(--cf-emerald); opacity: 1; }

  .cf-seg-label {
    font-size: 13px;
  }

  /* Required switch */
  .cf-switch { margin: 0; }
  .cf-switch label { font-size: 0; }
  .cf-switch .lever { margin: 0; }
  /* Tint the active switch to match the emerald accent without overriding globally */
  .cf-switch input[type=checkbox]:checked + .lever {
    background-color: rgba(0, 171, 142, 0.35);
  }
  .cf-switch input[type=checkbox]:checked + .lever::after {
    background-color: var(--cf-emerald);
  }

  /* Remove */
  .cf-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    color: var(--cf-muted);
    transition: color 120ms ease, background 120ms ease;
  }
  .cf-remove:hover {
    color: var(--cf-ink);
    background: rgba(0, 0, 0, 0.05);
  }

  /* Reorder */
  .cf-col-order {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
  }
  .cf-order-btn {
    appearance: none;
    background: transparent;
    border: 0;
    padding: 0;
    width: 22px;
    height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    cursor: pointer;
    color: var(--cf-muted);
    font-size: 10px;
    transition: color 120ms ease, background 120ms ease;
  }
  .cf-order-btn:hover:not(:disabled) {
    color: var(--cf-ink);
    background: rgba(0, 0, 0, 0.05);
  }
  .cf-order-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  /* Toolbar / actions */
  .cf-toolbar {
    padding: 10px 0 6px;
  }
  .cf-add-btn {
    padding: 0 12px;
    height: 32px;
    line-height: 32px;
    color: var(--cf-ink);
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
  }
  .cf-add-btn:disabled { color: var(--cf-muted); cursor: not-allowed; }
  .cf-add-btn .cf-slots {
    margin-left: 4px;
    color: var(--cf-muted);
    font-weight: 400;
  }

  .cf-actions {
    display: flex;
    align-items: center;
    gap: 18px;
    padding-top: 10px;
    margin-top: 6px;
    border-top: 1px solid var(--cf-line);
  }
  .cf-cancel { font-size: 13px; }

  .cf-empty-edit {
    padding: 16px 0 8px;
    font-size: 13px;
    text-align: center;
  }
</style>
